import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { authRedirectTo, supabase } from "./supabaseClient";
import {
  RECOVERY_LINK_ERROR,
  RESET_PARAM,
  authErrorMessage,
  parseAuthReturn,
} from "./authUrl";
import { friendlyError } from "./utils/errors";

const AuthContext = createContext(null);

/** Prevent React Strict Mode from exchanging the same OAuth code twice. */
let oauthHandlingPromise = null;

// Drops the Auth query and fragment (codes, errors, tokens) from the address bar.
function clearOAuthParamsAndGoInscribir() {
  const path = window.location.pathname || "/";
  window.history.replaceState({}, document.title, path);
  window.location.hash = "#/mis-equipos";
}

export async function handleOAuthReturn() {
  const { code, flowId, recovery, error: urlError, hasAuthParams } =
    parseAuthReturn(window.location.search, window.location.hash);

  if (urlError) {
    clearOAuthParamsAndGoInscribir();
    return {
      session: null,
      recovery: false,
      error: authErrorMessage(urlError, { recovery }),
    };
  }

  if (!code && hasAuthParams) {
    // A token-bearing fragment we never consume (PKCE uses ?code=).
    clearOAuthParamsAndGoInscribir();
  }

  if (!code) {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return { session, recovery: false, error: null };
  }

  // exchangeCodeForSession expects the CODE only — not the full URL.
  const { data, error } = await supabase.auth.exchangeCodeForSession(
    code,
    flowId ? { flowId } : undefined
  );

  clearOAuthParamsAndGoInscribir();

  if (error) {
    // If detectSessionInUrl already exchanged it, session may still exist.
    const {
      data: { session: existing },
    } = await supabase.auth.getSession();
    if (existing) {
      return { session: existing, recovery, error: null };
    }
    return {
      session: null,
      recovery: false,
      error: recovery
        ? RECOVERY_LINK_ERROR
        : friendlyError(error, "No se pudo completar el inicio de sesión. Inténtalo de nuevo."),
    };
  }

  return { session: data.session, recovery, error: null };
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  // True after arriving from a password-reset email: the page asks for a new password.
  const [recoveryMode, setRecoveryMode] = useState(false);

  const refreshProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      return null;
    }
    const { data, error } = await supabase
      .from("profiles")
      .select(
        "id, role, full_name, phone, notify_email, notify_sms, notify_whatsapp, created_at"
      )
      .eq("id", userId)
      .maybeSingle();
    if (error) {
      console.error("Failed to load profile:", error);
      setProfile(null);
      return null;
    }

    if (data && !data.full_name) {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const metaName =
        user?.user_metadata?.full_name || user?.user_metadata?.name || null;
      if (metaName) {
        const { data: updated } = await supabase
          .from("profiles")
          .update({ full_name: String(metaName).trim() })
          .eq("id", userId)
          .select(
            "id, role, full_name, phone, notify_email, notify_sms, notify_whatsapp, created_at"
          )
          .maybeSingle();
        if (updated) {
          setProfile(updated);
          return updated;
        }
      }
    }

    setProfile(data);
    return data;
  }, []);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      if (!oauthHandlingPromise) {
        oauthHandlingPromise = handleOAuthReturn().finally(() => {
          // Allow future logins; keep short debounce so Strict Mode remount shares work.
          setTimeout(() => {
            oauthHandlingPromise = null;
          }, 0);
        });
      }

      const { session: next, error, recovery } = await oauthHandlingPromise;
      if (!mounted) return;

      if (error) setAuthError(error);
      if (recovery && next) setRecoveryMode(true);
      setSession(next);
      await refreshProfile(next?.user?.id);
      if (mounted) setLoading(false);
    };

    init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, next) => {
      if (event === "PASSWORD_RECOVERY") setRecoveryMode(true);
      if (event === "SIGNED_OUT") setRecoveryMode(false);
      setSession(next);
      refreshProfile(next?.user?.id);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [refreshProfile]);

  const signIn = useCallback(async (email, password) => {
    setAuthError(null);
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) throw error;
    return data;
  }, []);

  const signUp = useCallback(async (email, password, contact = {}) => {
    setAuthError(null);
    const fullName = (contact.fullName || "").trim();
    const phone = (contact.phone || "").trim();
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        // Confirmation link returns to this site (not the project's Site URL).
        emailRedirectTo: authRedirectTo(),
        data: {
          full_name: fullName || null,
          phone: phone || null,
        },
      },
    });
    if (error) throw error;

    if (data.session?.user?.id && (fullName || phone)) {
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          ...(fullName ? { full_name: fullName } : {}),
          ...(phone ? { phone } : {}),
        })
        .eq("id", data.session.user.id);
      if (profileError) {
        console.error("Failed to save profile contact:", profileError);
      }
    }

    return data;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setAuthError(null);
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: authRedirectTo(),
        queryParams: {
          prompt: "select_account",
        },
      },
    });
    if (error) throw error;
    return data;
  }, []);

  /** Emails a reset link that returns here and opens the "new password" form. */
  const requestPasswordReset = useCallback(async (email) => {
    setAuthError(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${authRedirectTo()}?${RESET_PARAM}=1`,
    });
    if (error) throw error;
  }, []);

  const updatePassword = useCallback(async (password) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
    // Recovery mode stays on so the page can confirm the change; it is
    // dismissed with clearRecoveryMode().
  }, []);

  const clearRecoveryMode = useCallback(() => setRecoveryMode(false), []);

  const signOut = useCallback(async () => {
    setAuthError(null);
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  const clearAuthError = useCallback(() => setAuthError(null), []);

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      role: profile?.role ?? null,
      loading,
      authError,
      clearAuthError,
      recoveryMode,
      clearRecoveryMode,
      signIn,
      signUp,
      signInWithGoogle,
      requestPasswordReset,
      updatePassword,
      signOut,
      refreshProfile,
    }),
    [
      session,
      profile,
      loading,
      authError,
      clearAuthError,
      recoveryMode,
      clearRecoveryMode,
      signIn,
      signUp,
      signInWithGoogle,
      requestPasswordReset,
      updatePassword,
      signOut,
      refreshProfile,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
